// N-15b: 通知先の LINE 宛先ピッカー。自テナントの顧客（`users`）を名前で部分一致検索して 1 人選ぶ。
// 候補は `lineUserId` あり & 未ブロックのみ（`searchRecipientCustomers`）。
// tenant-reservation の CustomerLinkPicker（M-121）と同じ「全件をクライアントで絞る」方式。

import { useId, useMemo, useState } from 'react';
import type { AppUser } from '@kawai/shared';
import { Button, Input } from '@kawai/ui';
import {
  recipientCustomerLabel,
  searchRecipientCustomers,
} from '../../lib/recipientCustomerSearch.js';
import type { AllUsersStatus } from '../../lib/hooks/useAllUsers.js';

export interface CustomerPickerProps {
  users: ReadonlyArray<AppUser>;
  status: AllUsersStatus;
  /** 選択中の lineUserId。未選択は null。 */
  value: string | null;
  /** 選択中の表示名（保存済み行の `lineDisplayName` / 一覧に無い時のフォールバック）。 */
  valueLabel?: string;
  onChange: (user: AppUser | null) => void;
  disabled?: boolean;
}

export function CustomerPicker({
  users,
  status,
  value,
  valueLabel,
  onChange,
  disabled,
}: CustomerPickerProps) {
  const [query, setQuery] = useState('');
  const inputId = useId();
  const listId = useId();

  const selected = useMemo(
    () => (value ? users.find((u) => u.lineUserId === value) : undefined),
    [users, value],
  );
  const results = useMemo(() => searchRecipientCustomers(users, query), [users, query]);

  return (
    <div className="space-y-2" data-testid="customer-picker">
      <label htmlFor={inputId} className="block text-label font-medium text-ink-2">
        LINE 宛先（顧客一覧から選ぶ / 任意）
      </label>
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-brand-tint px-3.5 py-2 text-body text-brand-ink">
          <span data-testid="customer-picker-selected">
            {selected ? recipientCustomerLabel(selected) : (valueLabel ?? value)}
            {!selected && status === 'ready' && (
              <span className="ml-1 text-label text-ink-3">
                （現在の顧客一覧に見つかりません）
              </span>
            )}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            解除
          </Button>
        </div>
      ) : (
        <>
          <Input
            id={inputId}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="名前で検索（LINE 表示名 / 予約氏名）"
            disabled={disabled || status === 'error'}
            aria-controls={listId}
            autoComplete="off"
          />
          {status === 'loading' && (
            <p className="text-label text-ink-3">顧客一覧を読み込み中…</p>
          )}
          {status === 'error' && (
            <p className="text-label text-danger-ink">顧客一覧を読み込めませんでした。</p>
          )}
          {query.trim() && status === 'ready' && results.length === 0 && (
            <p className="text-label text-ink-3">
              該当する顧客がいません（LINE を友だち追加済みで、ブロックしていない人だけ選べます）。
            </p>
          )}
          {results.length > 0 && (
            <ul id={listId} className="max-h-48 space-y-1 overflow-y-auto">
              {results.map((u) => (
                <li key={u.uid}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      onChange(u);
                      setQuery('');
                    }}
                    className="w-full rounded-lg bg-surface-2 px-3.5 py-1.5 text-left text-body text-ink hover:bg-surface-3 disabled:opacity-50"
                  >
                    {recipientCustomerLabel(u)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
